import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import LoadingOverlay from '../../components/ui/LoadingOverlay';
import MemberProfileHeader from './components/MemberProfileHeader';
import PersonalDetailsSection from './components/PersonalDetailsSection';
import ContactInformationSection from './components/ContactInformationSection';
import AddressInformationSection from './components/AddressInformationSection';
import DeleteConfirmationModal from './components/DeleteConfirmationModal';
import NotificationToast from './components/NotificationToast';

const MemberDetails = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const memberId = searchParams?.get('id');

  // State management
  const [member, setMember] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [notification, setNotification] = useState(null);

  // Mock member data
  const mockMembers = [
    {
      id: "M001",
      firstName: "Maria",
      lastName: "Silva Santos",
      dateOfBirth: "1985-03-15",
      gender: "female",
      maritalStatus: "married",
      occupation: "employed",
      cpf: "123.456.789-01",
      voterId: "1234 5678 9012",
      membershipType: "Premium",
      isVoterRegistered: true,
      registrationDate: "2023-01-15T10:30:00Z",
      lastModified: "2025-01-05T14:20:00Z",
      profileImage: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=400&h=400&fit=crop&crop=face",
      emails: [
        { email: "maria.silva@email.com", isPrimary: true },
        { email: "maria.work@company.com", isPrimary: false }
      ],
      phones: [
        { phone: "(11) 99999-8888", type: "mobile", isPrimary: true },
        { phone: "(11) 3333-4444", type: "home", isPrimary: false }
      ],
      addresses: [
        {
          street: "Rua das Flores",
          number: "123",
          complement: "Apto 45",
          neighborhood: "Centro",
          city: "São Paulo",
          state: "SP",
          zipCode: "01234-567",
          type: "home",
          isPrimary: true
        },
        {
          street: "Avenida Paulista",
          number: "1000",
          complement: "Sala 201",
          neighborhood: "Bela Vista",
          city: "São Paulo",
          state: "SP",
          zipCode: "01310-100",
          type: "work",
          isPrimary: false
        }
      ]
    },
    {
      id: "M002",
      firstName: "João",
      lastName: "Oliveira Costa",
      dateOfBirth: "1978-07-22",
      gender: "male",
      maritalStatus: "single",
      occupation: "self-employed",
      cpf: "987.654.321-09",
      voterId: "9876 5432 1098",
      membershipType: "Regular",
      isVoterRegistered: true,
      registrationDate: "2023-02-20T09:15:00Z",
      lastModified: "2024-12-28T16:45:00Z",
      profileImage: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=400&fit=crop&crop=face",
      emails: [
        { email: "joao.costa@email.com", isPrimary: true }
      ],
      phones: [
        { phone: "(21) 98888-7777", type: "mobile", isPrimary: true }
      ],
      addresses: [
        {
          street: "Rua Copacabana",
          number: "456",
          complement: "",
          neighborhood: "Copacabana",
          city: "Rio de Janeiro",
          state: "RJ",
          zipCode: "22070-001",
          type: "home",
          isPrimary: true
        }
      ]
    }
  ];

  // Load member data
  useEffect(() => {
    const loadMember = async () => {
      setIsLoading(true);
      try {
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        const foundMember = mockMembers?.find(m => m?.id === memberId);
        if (foundMember) {
          setMember(foundMember);
          setFormData(foundMember);
        } else {
          showNotification('error', 'Member Not Found', 'The requested member could not be found.');
          navigate('/member-list');
        }
      } catch (error) {
        showNotification('error', 'Error Loading Member', 'Failed to load member details. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    if (memberId) {
      loadMember();
    } else {
      navigate('/member-list');
    }
  }, [memberId, navigate]);

  // Form handling
  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear field error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  // Validation
  const validateForm = () => {
    const newErrors = {};

    // Required fields
    if (!formData?.firstName?.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!formData?.lastName?.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    if (!formData?.dateOfBirth) {
      newErrors.dateOfBirth = 'Date of birth is required';
    }
    if (!formData?.cpf?.trim()) {
      newErrors.cpf = 'CPF is required';
    }

    // CPF format validation
    if (formData?.cpf && !/^\d{3}\.\d{3}\.\d{3}-\d{2}$/?.test(formData?.cpf)) {
      newErrors.cpf = 'CPF must be in format 000.000.000-00';
    }

    // Voter ID format validation
    if (formData?.voterId && !/^\d{4}\s\d{4}\s\d{4}$/?.test(formData?.voterId)) {
      newErrors.voterId = 'Voter ID must be in format 0000 0000 0000';
    }

    // Email validation
    formData?.emails?.forEach((emailObj, index) => {
      if (emailObj?.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(emailObj?.email)) {
        if (!newErrors?.emails) newErrors.emails = {};
        newErrors.emails[index] = 'Invalid email format';
      }
    });

    // Phone validation
    formData?.phones?.forEach((phoneObj, index) => {
      if (phoneObj?.phone && !/^\(\d{2}\)\s\d{4,5}-\d{4}$/?.test(phoneObj?.phone)) {
        if (!newErrors?.phones) newErrors.phones = {};
        newErrors.phones[index] = 'Phone must be in format (00) 00000-0000';
      }
    });

    // Address validation
    formData?.addresses?.forEach((address, index) => {
      const addressErrors = {};
      if (!address?.street?.trim()) addressErrors.street = 'Street is required';
      if (!address?.number?.trim()) addressErrors.number = 'Number is required';
      if (!address?.neighborhood?.trim()) addressErrors.neighborhood = 'Neighborhood is required';
      if (!address?.city?.trim()) addressErrors.city = 'City is required';
      if (!address?.state) addressErrors.state = 'State is required';
      if (!address?.zipCode?.trim()) addressErrors.zipCode = 'ZIP code is required';
      
      if (Object.keys(addressErrors)?.length > 0) {
        if (!newErrors?.addresses) newErrors.addresses = {};
        newErrors.addresses[index] = addressErrors;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  // Actions
  const handleEdit = () => {
    setIsEditing(true);
    setFormData({ ...member });
    setErrors({});
  };

  const handleCancel = () => {
    setIsEditing(false);
    setFormData({ ...member });
    setErrors({});
  };

  const handleSave = async () => {
    if (!validateForm()) {
      showNotification('error', 'Validation Error', 'Please correct the errors in the form.');
      return;
    }

    setIsSaving(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Update member data
      const updatedMember = {
        ...formData,
        lastModified: new Date()?.toISOString()
      };
      
      setMember(updatedMember);
      setIsEditing(false);
      showNotification('success', 'Member Updated', 'Member details have been successfully updated.');
    } catch (error) {
      showNotification('error', 'Update Failed', 'Failed to update member details. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    setIsDeleting(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      showNotification('success', 'Member Deleted', 'Member has been successfully deleted.');
      navigate('/member-list');
    } catch (error) {
      showNotification('error', 'Delete Failed', 'Failed to delete member. Please try again.');
      setIsDeleting(false);
    }
  };

  const showNotification = (type, title, message) => {
    setNotification({ type, title, message });
  };

  const closeNotification = () => {
    setNotification(null);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <LoadingOverlay 
          isVisible={true} 
          message="Carregando detalhes do membro..." 
          type="processing"
        />
      </div>
    );
  }

  if (!member) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="text-center">
              <h1 className="text-2xl font-semibold text-foreground mb-4">Membro não encontrado</h1>
              <p className="text-muted-foreground mb-6">O membro solicitado não foi encontrado.</p>
              <button
                onClick={() => navigate('/member-list')}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
              >
                Voltar para lista de membros
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Breadcrumb />
          
          <div className="space-y-6">
            <MemberProfileHeader
              member={member}
              isEditing={isEditing}
              onEdit={handleEdit}
              onSave={handleSave}
              onCancel={handleCancel}
              onDelete={handleDelete}
            />

            <PersonalDetailsSection
              member={member}
              isEditing={isEditing}
              formData={formData}
              onFormChange={handleFormChange}
              errors={errors}
            />

            <ContactInformationSection
              member={member}
              isEditing={isEditing}
              formData={formData}
              onFormChange={handleFormChange}
              errors={errors}
            />

            <AddressInformationSection
              member={member}
              isEditing={isEditing}
              formData={formData}
              onFormChange={handleFormChange}
              errors={errors}
            />
          </div>
        </div>
      </div>

      {/* Loading Overlay for Save */}
      <LoadingOverlay 
        isVisible={isSaving} 
        message="Salvando detalhes do membro..." 
        type="processing"
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        member={member}
        onConfirm={confirmDelete}
        onCancel={() => setShowDeleteModal(false)}
        isDeleting={isDeleting}
      />

      {/* Notification Toast */}
      <NotificationToast
        notification={notification}
        onClose={closeNotification}
      />
    </div>
  );
};

export default MemberDetails;